/**
 * Dark mode con persistencia en localStorage. Tres opciones: light, dark, system.
 */
import * as React from 'react';

type Theme = 'light' | 'dark' | 'system';
const KEY = 'kb-theme';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  resolved: 'light' | 'dark';
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

function resolveSystem(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function apply(theme: Theme): 'light' | 'dark' {
  const resolved = theme === 'system' ? resolveSystem() : theme;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  return resolved;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = React.useState<Theme>(
    () => (localStorage.getItem(KEY) as Theme | null) ?? 'system',
  );
  const [resolved, setResolved] = React.useState<'light' | 'dark'>(() => apply(theme));

  React.useEffect(() => {
    setResolved(apply(theme));
    localStorage.setItem(KEY, theme);
  }, [theme]);

  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (theme === 'system') setResolved(apply('system'));
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setThemeState, resolved }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme fuera de ThemeProvider');
  return ctx;
}
